import {
  BUILDING_TYPES,
  type CaseListItemDto,
  type ContactDto,
  formatPhone,
  type MeDto,
  type NipLookupDto,
  type PartyCorrespondenceDto,
  type PartyDto,
  SITE_ROLES,
  type SiteDto,
  VAT_STATUS_LABELS,
} from '@bqcrm/shared';
import { ActionIcon, Anchor, Badge, Button, Group, Modal, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBriefcase, IconFolderOpen, IconMail, IconMapPin, IconPencil, IconPlus, IconTrash, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can, canQuickCase } from '../../auth';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { QuickCaseModal } from '../../components/QuickCaseModal';
import { EmptyState, SectionCard } from '../../components/ui';
import { notifyError, notifyOk, notifyWarn } from '../../notify';
import { VatStatusBadge } from '../../components/Badges';
import { CaseRows } from '../CasesPage';
import { ContactForm, PartyForm, SiteForm } from './PartyForms';
import { PartyCorrespondence } from './PartyCorrespondence';
import { PartyDocuments } from './PartyDocuments';
import { ClientPortalCard } from '../case/ClientPortalCard';
import { ask } from '../../confirm';

const PARTY_TABS = ['dane', 'korespondencja', 'dokumenty'];

export function CaseMiniList({ items }: { items: CaseListItemDto[] }) {
  if (!items.length) return <EmptyState compact icon={<IconBriefcase size={18} />} title="Brak spraw" />;
  return <CaseRows items={items} showClient={false} />;
}

export function PartyPage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['party', id], queryFn: () => api<PartyDto>(`/parties/${id}`) });
  const [editing, edit] = useDisclosure();
  const [contact, setContact] = useState<ContactDto | 'new' | null>(null);
  const [newSite, siteModal] = useDisclosure();
  const [quickOpen, quick] = useDisclosure();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = can(me, 'klienci.edycja');
  // ?zakladka=korespondencja&watek=… (z powiadomienia) / ?zakladka=dokumenty
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('zakladka') ?? '';
  const tab = PARTY_TABS.includes(rawTab) ? rawTab : 'dane';
  const setTab = (v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (!v || v === 'dane') n.delete('zakladka');
        else n.set('zakladka', v);
        if (v !== 'korespondencja') n.delete('watek');
        return n;
      },
      { replace: true },
    );
  const corrQ = useQuery({
    queryKey: ['party-correspondence', id],
    queryFn: () => api<PartyCorrespondenceDto>(`/parties/${id}/correspondence`),
    refetchInterval: 60_000,
  });
  const delContact = useMutation({
    mutationFn: (c: ContactDto) => api(`/contacts/${c.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto osobę kontaktową');
      void qc.invalidateQueries({ queryKey: ['party', id] });
    },
    onError: notifyError,
  });
  const vatCheck = useMutation({
    mutationFn: () => api<NipLookupDto>(`/parties/${id}/vat-check`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.vat) notifyOk(`Biała lista VAT: ${VAT_STATUS_LABELS[r.vat.status]}`);
      else notifyWarn(r.vatError ?? 'Nie udało się sprawdzić statusu VAT');
      void qc.invalidateQueries({ queryKey: ['party', id] });
    },
    onError: notifyError,
  });

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <>
          <PageHeader
            crumbs={[{ label: 'Klienci', to: '/klienci' }, { label: q.data.displayName }]}
            title={q.data.displayName}
            badge={
              <Badge size="sm" variant="light" color={q.data.kind === 'firma' ? 'blue' : 'eko'}>
                {q.data.kind === 'firma' ? 'Firma' : 'Osoba prywatna'}
              </Badge>
            }
            description={
              <Stack gap={2} component="span">
                <span>
                  {[q.data.nip && `NIP ${q.data.nip}`, ...q.data.phones.map(formatPhone), ...q.data.emails].filter(Boolean).join(' · ') || 'Brak danych kontaktowych'}
                </span>
                <span>
                  {[q.data.street, [q.data.postalCode, q.data.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'Brak adresu korespondencyjnego'}
                </span>
              </Stack>
            }
            actions={
              <>
                {canEdit && (
                  <Button variant="light" leftSection={<IconPencil size={16} />} onClick={edit.open}>
                    Edytuj
                  </Button>
                )}
                {canQuickCase(me) && (
                  <Button leftSection={<IconPlus size={16} />} onClick={quick.open}>
                    Nowe zapytanie
                  </Button>
                )}
              </>
            }
          />
          <Group gap={4} mb="md">
            {q.data.kind === 'firma' && q.data.nip && (
              <>
                {q.data.vatCheck ? <VatStatusBadge check={q.data.vatCheck} /> : <Badge size="sm" variant="light" color="gray">VAT niesprawdzony</Badge>}
                {canEdit && (
                  <Button size="compact-xs" variant="subtle" loading={vatCheck.isPending} onClick={() => vatCheck.mutate()}>
                    Sprawdź na białej liście
                  </Button>
                )}
              </>
            )}
            <Badge size="sm" variant="light" color={q.data.consentReminders ? 'green' : 'gray'}>
              Przypomnienia: {q.data.consentReminders ? 'tak' : 'nie'}
            </Badge>
            <Badge size="sm" variant="light" color={q.data.consentMarketing ? 'green' : 'gray'}>
              Marketing: {q.data.consentMarketing ? 'tak' : 'nie'}
            </Badge>
          </Group>
          {q.data.notes && (
            <Text size="sm" mb="md" style={{ whiteSpace: 'pre-wrap' }}>
              {q.data.notes}
            </Text>
          )}

          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="dane" leftSection={<IconUsers size={14} />}>
                Dane i sprawy
              </Tabs.Tab>
              <Tabs.Tab
                value="korespondencja"
                leftSection={<IconMail size={14} />}
                rightSection={
                  corrQ.data?.unread ? (
                    <Badge size="xs" color="red" circle>
                      {corrQ.data.unread}
                    </Badge>
                  ) : null
                }
              >
                Korespondencja
              </Tabs.Tab>
              <Tabs.Tab value="dokumenty" leftSection={<IconFolderOpen size={14} />}>
                Dokumenty
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="korespondencja">
              <PartyCorrespondence partyId={id} me={me} cases={q.data.cases.map((c) => ({ id: c.id, number: c.number, title: c.title }))} />
            </Tabs.Panel>
            <Tabs.Panel value="dokumenty">
              <PartyDocuments partyId={id} />
            </Tabs.Panel>
            <Tabs.Panel value="dane">
          {/* strefa klienta (docs/18) — wszystkie sprawy klienta; link także bez spraw */}
          <ClientPortalCard partyId={id} />
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <SectionCard
              title="Osoby kontaktowe"
              icon={<IconUsers size={16} />}
              actions={
                canEdit && (
                  <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => setContact('new')}>
                    Dodaj osobę
                  </Button>
                )
              }
            >
              {q.data.contacts.length === 0 && <EmptyState compact icon={<IconUsers size={18} />} title="Brak osób kontaktowych" hint="Kontaktem jest sam klient." />}
              {q.data.contacts.map((c) => (
                <Group key={c.id} justify="space-between" py={4} wrap="nowrap">
                  <div>
                    <Text size="sm" fw={600}>
                      {c.fullName} {c.role && <Text span c="dimmed" size="xs">({c.role})</Text>}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[c.phone && formatPhone(c.phone), c.email, c.notes].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                  {canEdit && (
                    <Group gap={2}>
                      <ActionIcon variant="subtle" onClick={() => setContact(c)} aria-label="Edytuj">
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label="Usuń"
                        onClick={() => {
                          void ask(`Osoba „${c.fullName}” zniknie z listy kontaktów klienta.`, { title: 'Usunąć osobę kontaktową?', confirm: 'Usuń', danger: true }).then((ok) => ok && delContact.mutate(c));
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                </Group>
              ))}
            </SectionCard>

            <SectionCard
              title="Lokalizacje"
              icon={<IconMapPin size={16} />}
              actions={
                canEdit && (
                  <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={siteModal.open}>
                    Dodaj lokalizację
                  </Button>
                )
              }
            >
              {q.data.sites.length === 0 && <EmptyState compact icon={<IconMapPin size={18} />} title="Brak lokalizacji" />}
              {q.data.sites.map((s: SiteDto) => (
                <Group key={s.id} gap="xs" py={4} wrap="nowrap" align="flex-start">
                  <IconMapPin size={16} style={{ marginTop: 3 }} />
                  <div>
                    <Anchor component={Link} to={`/lokalizacje/${s.id}`} size="sm" fw={600}>
                      {s.address}
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      {[s.buildingType && BUILDING_TYPES[s.buildingType], s.usableArea && `${s.usableArea} m²`, ...s.links.filter((l) => l.partyId === q.data.id).map((l) => SITE_ROLES[l.role])]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </div>
                </Group>
              ))}
            </SectionCard>
          </SimpleGrid>

          <SectionCard title={`Sprawy (${q.data.cases.length})`} icon={<IconBriefcase size={16} />} padding={0}>
            <CaseMiniList items={q.data.cases} />
          </SectionCard>
            </Tabs.Panel>
          </Tabs>

          <Modal opened={editing} onClose={edit.close} title="Edycja klienta" size="lg">
            <PartyForm party={q.data} onDone={edit.close} onCancel={edit.close} />
          </Modal>
          <Modal opened={contact !== null} onClose={() => setContact(null)} title={contact === 'new' ? 'Nowa osoba kontaktowa' : 'Edycja osoby kontaktowej'} size="lg">
            {contact !== null && <ContactForm partyId={id} contact={contact === 'new' ? null : contact} onDone={() => setContact(null)} onCancel={() => setContact(null)} />}
          </Modal>
          <Modal opened={newSite} onClose={siteModal.close} title="Nowa lokalizacja" size="lg">
            <SiteForm
              site={null}
              partyId={id}
              onCancel={siteModal.close}
              onDone={(s) => {
                siteModal.close();
                navigate(`/lokalizacje/${s.id}`);
              }}
            />
          </Modal>
          <QuickCaseModal opened={quickOpen} onClose={quick.close} me={me} presetClient={q.data} />
        </>
      )}
    </QueryState>
  );
}
